import React, { useEffect } from "react";
import { Box, Grid, Tooltip } from "@mui/material";
import {
  LocationOnOutlined,
  PhoneOutlined,
  InfoOutlined,
  StorageOutlined,
  WarehouseOutlined,
  EmailOutlined,
  ContactMailOutlined,
} from "@mui/icons-material";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";

import { useWarehouseDetailContext } from "contexts/WarehouseDetailContext";
import InformationTile from "components/Shared/InformationTile";
import InfoGridSkeleton from "components/Shared/Skeleton/InfoGridSkeleton";
import Status from "components/Shared/Status";
import { Link } from "react-router-dom";

// Component to dynamically update the map's center
const MapUpdater = ({ location }) => {
  const map = useMap();

  useEffect(() => {
    if (location?.lat && location?.lon) {
      map.setView([location.lat, location.lon], map.getZoom());
    }
  }, [location, map]);

  return null;
};

const WarehouseBasicDetail = () => {
  const { warehouseDetail, loadingWarehouse } = useWarehouseDetailContext();

  const location = warehouseDetail?.location;

  const hasLocation = location?.lat && location?.lon;

  return !loadingWarehouse ? (
    <Box padding={3}>
      <Grid container spacing={2}>
        <Grid item xs={3}>
          <InformationTile
            icon={() => <WarehouseOutlined color="primary" />}
            title="Warehouse ID"
            subtitle={warehouseDetail?.warehouse_id}
          />
        </Grid>
        <Grid item xs={3}>
          <InformationTile
            icon={() => <WarehouseOutlined color="primary" />}
            title="Warehouse Name"
            subtitle={warehouseDetail?.name}
          />
        </Grid>
        <Grid item xs={3}>
          <InformationTile
            icon={() => <ContactMailOutlined color="primary" />}
            title="Contact Details"
          >
            {warehouseDetail?.contact_details?.length > 0
              ? warehouseDetail?.contact_details.map((contact, index) => (
                  <Box
                    key={index}
                    sx={{
                      mb: 1,
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <EmailOutlined color="action" fontSize="small" />
                      {contact?.email}
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <PhoneOutlined color="action" fontSize="small" />
                      {contact?.phoneNumber}
                    </Box>
                  </Box>
                ))
              : "No contact details available"}
          </InformationTile>
        </Grid>
        <Grid item md={3}>
          <InformationTile
            icon={() => <WarehouseOutlined color="primary" />}
            title="Branch"
            subtitle={
              <Box>
                {warehouseDetail?.branch?.name ? (
                  <Tooltip title={"View Branch"} arrow>
                    <Link
                      to={`/app/crm/branch/${warehouseDetail?.branch?._id}/detail`}
                      className="text-inherit underline"
                    >
                      {warehouseDetail.branch.name}
                    </Link>
                  </Tooltip>
                ) : (
                  "Not Available"
                )}
              </Box>
            }
          />
        </Grid>
        <Grid item xs={3}>
          <InformationTile
            icon={() => <StorageOutlined color="primary" />}
            title="Capacity"
            subtitle={`${warehouseDetail?.capacity || 0} units`}
          />
        </Grid>
        <Grid item xs={3}>
          <InformationTile
            icon={() => <InfoOutlined color="primary" />}
            title="Operational Status"
          >
            <Status isActive={warehouseDetail?.operational_status} />
          </InformationTile>
        </Grid>
        {/* Map Section */}
        <Grid item xs={12}>
          {hasLocation ? (
            <MapContainer
              center={[location.lat, location.lon]}
              zoom={15}
              style={{ height: "400px", width: "100%", borderRadius: "8px" }}
            >
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

              {/* Updates the center when location changes */}
              <MapUpdater location={location} />

              <Marker position={[location.lat, location.lon]}>
                <Popup>{warehouseDetail?.name || "Warehouse Location"}</Popup>
              </Marker>
            </MapContainer>
          ) : (
            <InformationTile
              icon={() => <LocationOnOutlined color="primary" />}
              title="Location"
              subtitle="Location data not available"
            />
          )}
        </Grid>
      </Grid>
    </Box>
  ) : (
    <InfoGridSkeleton />
  );
};

export default WarehouseBasicDetail;
